/**
 * GET /api/v5/brain/category?session_id=&category= once per diagnosis (brain_api.schema.json category_bundle): the category's
 * graph, hierarchy, cause list and lines. Optional for the panel: without it the panel still renders
 * from tse:diag_state, with fewer labels. A failure is kept as null and retried on the next diagnosis.
 * `learnedKey` (learningKeyOf(diag_state)) refetches it when a case is learned: the Learning tab's 'Stored now' counts come
 * from the bundle's cause nodes, which a publish changes. The old bundle stays shown until the new one arrives.
 */
import { useEffect, useState } from "react";

import { BRAIN_ENDPOINTS, brainGet } from "../../vendor/brain";

const BUNDLE_KIND = "category_bundle";

/** A string that changes when learning.last_learned changes (a publish or an SME undo), "" when nothing was learned. */
export function learningKeyOf(diagState) {
  const ll = diagState?.learning?.last_learned;
  return ll ? [ll.cause_key, ll.at, ll.state || ""].join("|") : "";
}

export default function useCategoryBundle(sessionId, diagnosisId, category, learnedKey = "") {
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    setBundle(null);
  }, [sessionId, diagnosisId, category]);

  useEffect(() => {
    if (!sessionId || !diagnosisId || !category) return undefined;
    let active = true;
    brainGet(BRAIN_ENDPOINTS.CATEGORY, { category })
      .then((res) => {
        if (active && res.ok && res.data?.kind === BUNDLE_KIND) setBundle(res.data);
      })
      .catch(() => {});      // a failed refetch keeps the bundle shown; a new diagnosis already cleared it above
    return () => { active = false; };
  }, [sessionId, diagnosisId, category, learnedKey]);

  return bundle;
}
